import moment from 'moment';
import _ from 'lodash';

const Calculations = {
    hoursAgo: (hours = 4) => moment().subtract(hours, 'hours').valueOf(),

    minutesUntilNextVisit: ({lastVisitTime, thresholds}) => {
    // Figure out when we need to visit next
        const nextVisitTime = lastVisitTime + thresholds.visits.requiredMinutes*1000*60;
        return Math.round((nextVisitTime - Date.now()) / 1000 / 60);
    },

    movementStats: (movements = []) => {
        const significantMovements = movements.filter((movement) => movement.isSignificant);
        const movementCount = significantMovements.length;
        const lastMovementTime = _.max(_.map(significantMovements, 'timestamp'));

        return {movementCount, lastMovementTime};
    },

    calculateAgitation: ({respiration, movements}, atTime = Date.now()) => {
        const recentRespiration = respiration.filter(({timestamp}) =>
            moment(timestamp)
                .isBetween(
                    moment(atTime).subtract(90, 'seconds'),
                    moment(atTime).add(90, 'seconds')
                )
        );
        const recentMovements = movements.filter(({timestamp}) =>
            moment(timestamp)
                .isBetween(
                    moment(atTime).subtract(90, 'seconds'),
                    moment(atTime).add(90, 'seconds')
                )
        );

        if (recentRespiration.length === 0 || recentMovements.length === 0) { return false; }

        const averageRespiration = _.meanBy(recentRespiration, 'rpm');
        const {movementCount} = Calculations.movementStats(recentMovements);

        // 5 minutes at least 4 movement
        return averageRespiration >= 24 && movementCount >= 2;
    },

    smoothData: (data, smoothingInterval = 5) => {
        const neighborCount = Math.floor(smoothingInterval/2);

        return data
            .map((item) => ({...item, value: item.value ? 1 : 0}))
            .slice(neighborCount, -neighborCount)
            .map(({timestamp}, i) => ({
                timestamp: timestamp,
                value: Boolean(Math.round(_.meanBy(data.slice(i, i+5), 'value'), 0)),
                dateString: moment(timestamp).toString()
            }));
    },

    determineOOOTransitions: (data) => {
        const transitions = [];

        data.slice(0, -1).forEach(({timestamp, value}, i) => {
            if (value !== data[i+1].value) {
                transitions.push({
                    timestamp,
                    type: value ? 'START' : 'END',
                    dateString: moment(timestamp).toString()
                });
            }
        });

        return transitions;
    },

    determineTransitions: (data) => {
        const transitions = [];

        data.slice(0, -1).forEach(({timestamp, value}, i) => {
            if (value !== data[i+1].value) {
                transitions.push({
                    timestamp,
                    type: value ? 'END' : 'START',
                    dateString: moment(timestamp).toString()
                });
            }
        });

        return transitions;
    },

    countVisitAlerts: ({visitTransitions, thresholds, endTime = Date.now()}) => {
        const soon = (thresholds.visit) ? parseInt(thresholds.visit.visit_soon, 10) * 60*1000 : 0;
        const required = (thresholds.visit) ? parseInt(thresholds.visit.require_visit, 10) * 60*1000 : 0;
        const transitions = _.cloneDeep(visitTransitions);

        transitions.forEach(({type, timestamp}, index) => {
            const previous = _.get(transitions, index-1);
            if (type === 'START' && previous) {
                let insertAt = index;

                if (timestamp - previous.timestamp > required-soon) {
                    transitions.splice(
                        insertAt,
                        0,
                        {timestamp: previous.timestamp+required-soon-4, type: 'START', alert: 'visit-warn'},
                        {timestamp: _.min([previous.timestamp+required, timestamp])-3, type: 'END', alert: 'visit-warn'},
                    );
                    insertAt += 2;
                }

                if (timestamp - previous.timestamp > required) {
                    transitions.splice(
                        insertAt,
                        0,
                        {timestamp: previous.timestamp+required-2, type: 'START', alert: 'visit-error'},
                        {timestamp: timestamp-1, type: 'END', alert: 'visit-error'},
                    );
                }
            }
        });

        const lastVisitTransition = _.last(transitions);

        if (_.get(lastVisitTransition, 'type') === 'END') {
            const timestamp = endTime;

            if (timestamp - lastVisitTransition.timestamp > required-soon) {
                transitions.push(
                    {timestamp: lastVisitTransition.timestamp+required-soon-4, type: 'START', alert: 'visit-warn'},
                    {timestamp: _.min([lastVisitTransition.timestamp+required, timestamp])-3, type: 'END', alert: 'visit-warn'},
                );
            }

            if (timestamp - lastVisitTransition.timestamp > required) {
                transitions.push(
                    {timestamp: lastVisitTransition.timestamp+required-2, type: 'START', alert: 'visit-error'},
                    {timestamp: timestamp-1, type: 'END', alert: 'visit-error'},
                );
            }
        }

        return {
            visitSoonCount: transitions.filter(({type, alert}) => type === 'START' && alert === 'visit-warn').length,
            visitRequiredCount: transitions.filter(({type, alert}) => type === 'START' && alert === 'visit-error').length,
        };
    },
    calculateLowerPoints: (source, threshold) => _.compact(
        _.flatten(
            _.map(source, (point, i) => {
                if (i === 0) {
                    return point.y < threshold ? point : {t: point.t, y: NaN};
                }

                const p0 = source[i - 1];
                const p1 = point;

                if (p1.y <= threshold && p0.y <= threshold) {
                    return {
                        t: p1.t,
                        y: p1.y
                    };
                } else if (p1.y <= threshold && p0.y > threshold) {
                    const slope = (p1.y - p0.y) / (p1.t - p0.t);

                    return [
                        {
                            t: (threshold - p0.y) / slope + p0.t,
                            y: threshold
                        },
                        {
                            t: p1.t,
                            y: p1.y
                        }
                    ];
                } else if (p1.y > threshold && p0.y <= threshold) {
                    const slope = (p1.y - p0.y) / (p1.t - p0.t);

                    return [
                        {
                            t: (threshold - p0.y) / slope + p0.t,
                            y: threshold
                        },
                        {
                            t: p1.t,
                            y: NaN
                        }
                    ];
                }
                return {
                    t: p1.t,
                    y: NaN
                };
            })
        )
    ),
    calculateUpperPoints: (source, threshold) => _.compact(
        _.flatten(
            _.map(source, (point, i) => {
                if (i === 0) {
                    return point.y > threshold ? point : {t: point.t, y: NaN};
                }

                const p0 = source[i - 1];
                const p1 = point;

                if (p1.y >= threshold && p0.y >= threshold) {
                    return {
                        t: p1.t,
                        y: p1.y
                    };
                } else if (p1.y >= threshold && p0.y < threshold) {
                    const slope = (p1.y - p0.y) / (p1.t - p0.t);

                    return [
                        {
                            t: (threshold - p0.y) / slope + p0.t,
                            y: threshold
                        },
                        {
                            t: p1.t,
                            y: p1.y
                        }
                    ];
                } else if (p1.y < threshold && p0.y >= threshold) {
                    const slope = (p1.y - p0.y) / (p1.t - p0.t);

                    return [
                        {
                            t: (threshold - p0.y) / slope + p0.t,
                            y: threshold
                        },
                        {
                            t: p1.t,
                            y: NaN
                        }
                    ];
                }
                return {
                    t: p1.t,
                    y: NaN
                };
            })
        )
    )
};

export default Calculations;
